import { Box, Button, InputAdornment, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { FC, useCallback, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import {
  UsuarioFamily,
  createUsuarioFamily,
  deleteUsuarioFamily,
  searchUsuariosFamily,
  updateUsuarioFamily,
} from "src/apis/eezyfamily";
import PageContainer from "src/pages/components/PageContainer";
import { isUserAdministrator } from "src/utils/auth";
import UsuariosTable from "./components/UsuariosTable";
import UsuarioModal from "./components/UsuarioModal";

const EFUsuariosPage: FC = (): JSX.Element => {
  const [entries, setEntries] = useState<UsuarioFamily[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UsuarioFamily | null>(null);

  const fetchUsers = useCallback(async (q: string) => {
    try {
      const data = await searchUsuariosFamily(q);
      setEntries(data);
      setError("");
    } catch (err: any) {
      setError(err?.message ?? "Erro ao carregar usuários.");
    }
  }, []);

  const debouncedSearch = useDebouncedCallback((q: string) => {
    fetchUsers(q);
  }, 300);

  useEffect(() => {
    fetchUsers("");
  }, [fetchUsers]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleEdit = (item: UsuarioFamily) => {
    setEditTarget(item);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditTarget(null);
    setModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    const user = entries.find((e) => e.id === id);
    if (
      !window.confirm(
        `Tem certeza que deseja excluir o usuário ${user?.name ?? ""}? Esta ação não pode ser desfeita.`
      )
    )
      return;
    try {
      const collaboratorId = user?.collaborator_id ?? id;
      await deleteUsuarioFamily(collaboratorId);
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err: any) {
      setError(err?.message ?? "Erro ao excluir.");
    }
  };

  const handleSaved = () => {
    setModalOpen(false);
    fetchUsers(searchQuery);
  };

  return (
    <PageContainer title="Usuários EezyFamily" error={error} guard={isUserAdministrator}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Buscar por nome ou e-mail..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Novo Usuário
        </Button>
      </Box>

      <UsuariosTable
        usuarios={entries}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <UsuarioModal
        open={modalOpen}
        editTarget={editTarget}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
        onCreate={createUsuarioFamily}
        onUpdate={updateUsuarioFamily}
      />
    </PageContainer>
  );
};

export default EFUsuariosPage;
